import { Button } from "@design-system/components/Button";
import { InputError } from "@design-system/components/Form/InputError";
import { InputLabel } from "@design-system/components/Form/InputLabel";
import {
  InputDescription,
  InputHint,
} from "@design-system/components/Form/Utils";
import { CalendarIcon, XIcon } from "@design-system/components/Icons";
import { Calendar } from "@design-system/ui/calendar";
import { Popover, PopoverTrigger } from "@design-system/ui/popover";
import { cn } from "@design-system/utils";
import { trans } from "@mongez/localization";
import {
  type FormControlProps,
  type InputRule,
  requiredRule,
  useFormControl,
} from "@mongez/react-form";
import { PopoverContent } from "@radix-ui/react-popover";
import { format, formatDate } from "date-fns";
import { type ReactNode, useState } from "react";
import { type Matcher } from "react-day-picker";

type DatePickerInputProps = FormControlProps & {
  id: string;
  label?: ReactNode;
  placeholder?: ReactNode;
  minDate?: Date;
  maxDate?: Date;
  format?: string;
  closeOnSelect?: boolean;
  collectFormat?: string;
  clearable?: boolean;
  hint?: ReactNode;
  description?: ReactNode;
};

export const defaultDateFormat = "dd-MM-yyyy";

export const minDateRule: InputRule = {
  name: "minDate",
  validate: ({ value, minDate, errorKeys }) => {
    if (!minDate) return;

    if (value < minDate) {
      return trans("validation.minDate", {
        input: errorKeys.name,
        date: formatDate(minDate, defaultDateFormat),
      });
    }
  },
};

export const maxDateRule: InputRule = {
  name: "maxDate",
  validate: ({ value, maxDate, errorKeys }) => {
    if (!maxDate) return;

    if (value > maxDate) {
      return trans("validation.maxDate", {
        input: errorKeys.name,
        date: formatDate(maxDate, defaultDateFormat),
      });
    }
  },
};

export function DatePickerInput({
  label,
  placeholder,
  clearable = true,
  format: formDate = defaultDateFormat,
  closeOnSelect = true,
  collectFormat,
  hint,
  description,
  ...props
}: DatePickerInputProps) {
  const [isOpened, setIsOpened] = useState(false);

  const { value, changeValue, id, error, disabled } = useFormControl(
    {
      rules: [requiredRule, minDateRule, maxDateRule],
      ...props,
    },
    {
      isCollectable({ value }) {
        return !!value;
      },
      collectValue({ value }) {
        if (!value) return;

        if (value.timestamp) {
          value = new Date(value.timestamp);
        }

        if (value instanceof Date) {
          return value.getTime();
        }

        return collectFormat ? format(value, collectFormat) : value;
      },
    },
  );

  const [date, setDate] = useState<Date | undefined>(() => {
    if (value.timestamp) return new Date(value.timestamp);

    if (value) return new Date(value);
    return undefined;
  });

  const changeDate = (date: Date | undefined) => {
    setDate(date);

    changeValue(date || "");
    if (closeOnSelect) {
      setIsOpened(false);
    }
  };

  const canBeCleared = props.required ? false : clearable && !!value;

  const clearCurrentDate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    changeDate(undefined);
  };

  const hiddenDays: Matcher[] = [];

  if (props.minDate) {
    hiddenDays.push({
      before: props.minDate,
    });
  }

  if (props.maxDate) {
    hiddenDays.push({
      after: props.maxDate,
    });
  }

  return (
    <div className="w-full space-y-1.5">
      {label && (
        <InputLabel required={props.required} htmlFor={id}>
          {label}
        </InputLabel>
      )}

      {description && <InputDescription>{description}</InputDescription>}

      <Popover open={isOpened} onOpenChange={setIsOpened}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "h-10 w-full justify-start px-3 text-left font-normal",
              "bg-background dark:bg-gray-800 hover:bg-background dark:hover:bg-gray-700",
              "border border-input dark:border-gray-600 hover:border-primary/50",
              "focus:outline-none focus:ring-2 focus:ring-primary/20",
              "text-gray-900 dark:text-gray-100",
              !date && "text-muted-foreground dark:text-gray-400",
              error && "border-destructive dark:border-red-400",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <div className="flex w-full items-center gap-2">
              <CalendarIcon className="size-4 opacity-50" />
              <span className="flex-1">
                {date ? format(date, formDate) : placeholder}
              </span>
              {canBeCleared && (
                <button
                  type="button"
                  onClick={clearCurrentDate}
                  className="border-0 bg-transparent p-0"
                >
                  <XIcon
                    id={`${id}-clear-btn`}
                    className="size-4 opacity-50 transition-colors hover:text-destructive hover:opacity-100"
                  />
                </button>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-50">
          <Calendar
            mode="single"
            selected={date}
            onSelect={changeDate}
            hidden={hiddenDays}
            disabled={disabled}
            defaultMonth={date}
          />
        </PopoverContent>
      </Popover>
      {hint && !error && <InputHint>{hint}</InputHint>}
      <InputError error={error} />
    </div>
  );
}
